// @ts-strict-ignore
/** @jsx jsx */
/** @jsxFrag null */
// eslint-disable-next-line no-restricted-imports
import {jsx} from 'panel';

import {ToggleEl} from 'common/components/toggle';
import {ToggleOption, ToggleTheme} from 'common/components/toggle/types';
import {ItemsMenuEl} from 'common/components/items-menu';
import {ScrollingCalendarEl} from 'common/components/scrolling-calendar';
import {ButtonEl} from 'common/components/button';
import {ButtonSize, ButtonTheme} from 'common/components/button/constants';
import {BannerEl} from 'common/components/banner';
import {TooltipEl} from 'common/components/tooltip';
import {InputEl} from 'common/components/input';
import {SelectEl} from 'common/components/select';
import {SelectTheme} from 'common/components/select/constants';
import {Visibility} from 'common/components/base/stackable';
import {Section, SectionItem} from 'common/components/items-menu/types';
import {DropMenuEl} from 'common/components/drop-menu';
import {MENU_HEIGHT_REDUCTION} from 'common/constants';

import type {DateRangePicker} from './index';
import {AdvancedMenuItem} from './util';

export function template(this: DateRangePicker) {
  const presets = this.helpers.getPresetMenuSections();
  const hasPresets = presets[0].items.length > 0;
  const showPresets = hasPresets && !this.attr(`force-hide-preset-menu`);

  const shouldHideCalendarPreview = this.helpers.shouldHideCalendarPreview();
  const previewSelection = this.helpers.getCalendarPreviewSelection();
  const selection = previewSelection ? null : this.helpers.getCalendarSelection();

  return (
    <div
      sel='.root'
      class={{
        expanded: this.state.showCustomControls,
        'no-presets': !showPresets,
      }}
    >
      {showPresets && (
        <div sel='.side-bar'>
          <ItemsMenuEl
            sel='.presets'
            attrs={{
              elref: `presetMenu`,
            }}
            props={{
              sections: presets,
            }}
            on={{
              clickItem: this.helpers.handleClickPreset,
            }}
          />
          <ItemsMenuEl
            sel='.custom'
            attrs={{
              elref: `customMenu`,
            }}
            props={{
              sections: [
                {
                  items: [
                    {
                      isSelected: this.state.showCustomControls,
                      label: `${this.t(`reports.date-range.presets.custom`)}…`,
                    },
                  ],
                },
              ],
            }}
            on={{
              clickItem: this.helpers.handleClickCustom,
            }}
          />
        </div>
      )}
      {(!showPresets || this.state.showCustomControls) && (
        <div
          sel='.custom-controls'
          attrs={{
            elref: `customControlsContainer`,
          }}
        >
          {!this.attr(`single-picker-mode`) && (
            <ToggleEl
              sel='.picker-modes'
              attrs={{
                theme: ToggleTheme.TabBar,
                selected: this.state.pickerMode,
                options: this.helpers.getPickerModeToggleOptions() as ToggleOption[],
                elref: `pickerModeToggle`,
              }}
              on={{
                select: this.helpers.handleClickPickerMode,
              }}
            />
          )}
          <div sel='.text-controls' key={this.state.pickerMode}>
            <slot attrs={{name: `header`}} />
            {(() => {
              switch (this.state.pickerMode) {
                case this.helpers.DatePickerMode.InTheLast:
                case this.helpers.DatePickerMode.RelativeFuture:
                  return (
                    <>
                      <div sel='.control-wrapper.in-the-last'>
                        <div sel='.control-row-wrapper'>
                          <InputEl
                            sel='.relative-window'
                            attrs={{
                              'auto-focus': true,
                              elref: `relativeWindowInput`,
                              'error-message': this.state.relativeInputErrorMsg,
                              max: String(this.helpers.getRelativeInputMax(this.state.relativeWindowUnit)),
                              min: `0`,
                              placeholder: this.t(`reports.date-range.custom.value-placeholder`),
                              'select-on-focus': true,
                              'text-align': this.helpers.TextAlign.Center,
                              type: `number`,
                              'validation-status': this.state.relativeInputErrorMsg ? `invalid` : ``,
                            }}
                            props={{
                              value: `${this.state.relativeWindowValue}`,
                            }}
                            on={{
                              input: this.helpers.handleLastNextWindowValueInput,
                              blur: this.helpers.handleInTheLastWindowAndOffsetValueBlur,
                            }}
                          />
                          <div sel='.unit-select'>
                            <SelectEl
                              attrs={{
                                theme: SelectTheme.Select,
                                'always-show-caret': true,
                                'selected-option': this.helpers.getTimeUnitLabel(
                                  this.state.relativeWindowUnit,
                                  this.helpers.getSelectedWindowValue() || 2,
                                ),
                                'menu-width': 0,
                                size: ButtonSize.Large,
                                elref: `relativeWindowUnitSelect`,
                              }}
                              props={{
                                sections: this.helpers.getWindowUnitMenuSections(),
                              }}
                              on={{
                                change: this.helpers.handleLastNextWindowUnitChange,
                              }}
                            />
                          </div>
                          {this.helpers.getSelectedOffset() && this.state.showEndingField && (
                            <TooltipEl>{this.t(`reports.date-range.custom.tooltip.change-unit-warning`)}</TooltipEl>
                          )}
                        </div>
                        {this.helpers.canUseRolling() &&
                          (this.state.showEndingField ? (
                            <>
                              <div sel='.control-row-wrapper'>
                                <span sel='.ending-label'>{this.t(`reports.date-range.custom.ending-date`)}</span>
                              </div>
                              <div sel='.control-row-wrapper'>
                                <InputEl
                                  sel='.ending-relative-offset'
                                  attrs={{
                                    type: `number`,
                                    min: `0`,
                                    placeholder: this.t(`reports.date-range.custom.value-placeholder`),
                                    'select-on-focus': true,
                                    'text-align': this.helpers.TextAlign.Center,
                                    elref: `relativeOffsetInput`,
                                  }}
                                  props={{
                                    value: `${this.state.relativeOffsetValue}`,
                                  }}
                                  on={{
                                    input: this.helpers.handleInTheLastOffsetValueInput,
                                    blur: this.helpers.handleInTheLastWindowAndOffsetValueBlur,
                                  }}
                                />
                                <span sel='.ending-unit-label'>
                                  {this.t(`reports.date-range.custom.duration-ago`, {
                                    duration: this.helpers.getTimeUnitLabel(
                                      this.state.relativeWindowUnit,
                                      this.state.relativeOffsetValue,
                                    ),
                                  })}
                                  <ButtonEl
                                    sel='.ending-hide'
                                    attrs={{
                                      icon: `circle-clear`,
                                      theme: ButtonTheme.Tertiary,
                                    }}
                                    on={{
                                      click: this.helpers.handleHideEndingClick,
                                    }}
                                  />
                                </span>
                              </div>
                            </>
                          ) : (
                            <div sel='.control-row-wrapper'>
                              {this.helpers.canUseRolling() && (
                                <div sel='.ending-show'>
                                  <ButtonEl
                                    attrs={{
                                      icon: `plus`,
                                      theme: ButtonTheme.Tertiary,
                                      disabled: !this.helpers.getSelectedOffset(),
                                      elref: `showEndingButton`,
                                    }}
                                    on={{
                                      click: this.helpers.handleShowEndingClick,
                                    }}
                                  >
                                    {this.t(`reports.date-range.custom.ending-date`)}
                                  </ButtonEl>
                                  {!this.helpers.getSelectedOffset() && (
                                    <TooltipEl>
                                      {this.t(`reports.date-range.custom.tooltip.change-unit`, {
                                        units: this.helpers.getTimeUnitLabel(this.state.relativeWindowUnit, 2),
                                      })}
                                    </TooltipEl>
                                  )}
                                </div>
                              )}
                            </div>
                          ))}
                      </div>
                    </>
                  );

                case this.helpers.DatePickerMode.Between:
                case this.helpers.DatePickerMode.On:
                  return (
                    <div sel='.control-wrapper.between'>
                      <div sel='.control-row-wrapper'>
                        {this.state.pickerMode === this.helpers.DatePickerMode.Between && (
                          <div sel='.date-time-label'>{this.t(`reports.date-range.custom.start`)}</div>
                        )}
                        <InputEl
                          sel='.date-text-input'
                          attrs={{
                            'auto-focus': false,
                            type: `text`,
                            placeholder: this.t(`reports.date-range.custom.date-placeholder`),
                            elref: `betweenFromInput`,
                          }}
                          props={{
                            value: this.state.fromDateText,
                          }}
                          on={{
                            input: this.helpers.handleFromDateInput,
                            focus: () => this.handleFromDateFocus(),
                            blur: () => this.handleFromDateBlur(),
                            keydown: this.helpers.handleFromDateArrowKeys,
                          }}
                        />
                        {this.state.isTimePickerEnabled && (
                          <InputEl
                            sel='.time-text-input'
                            attrs={{
                              'auto-focus': !this.state.fromTimeText,
                              type: `text`,
                              placeholder: this.t(`reports.date-range.custom.time-placeholder`),
                              elref: `betweenFromTimeInput`,
                            }}
                            props={{
                              value: this.state.fromTimeText,
                            }}
                            on={{
                              input: this.helpers.handleFromTimeInput,
                              blur: () => this.handleFromTimeBlur(),
                              keydown: this.helpers.handleFromTimeArrowKeys,
                            }}
                          />
                        )}
                      </div>
                      {this.state.pickerMode === this.helpers.DatePickerMode.Between && (
                        <div sel='.control-row-wrapper'>
                          <div sel='.date-time-label'>{this.t(`reports.date-range.custom.end`)}</div>
                          <InputEl
                            sel='.date-text-input'
                            attrs={{
                              type: `text`,
                              placeholder: this.t(`reports.date-range.custom.date-placeholder`),
                              elref: `betweenToInput`,
                            }}
                            props={{
                              value: this.state.toDateText,
                            }}
                            on={{
                              input: this.helpers.handleToDateInput,
                              focus: () => this.handleToDateFocus(),
                              blur: () => this.handleToDateBlur(),
                              keydown: this.helpers.handleToDateArrowKeys,
                            }}
                          />
                          {this.state.isTimePickerEnabled && (
                            <InputEl
                              sel='.time-text-input'
                              attrs={{
                                type: `text`,
                                placeholder: this.t(`reports.date-range.custom.time-placeholder`),
                                elref: `betweenToTimeInput`,
                              }}
                              props={{
                                value: this.state.toTimeText,
                              }}
                              on={{
                                input: this.helpers.handleToTimeInput,
                                blur: () => this.handleToTimeBlur(),
                                keydown: this.helpers.handleToTimeArrowKeys,
                              }}
                            />
                          )}
                        </div>
                      )}
                    </div>
                  );

                case this.helpers.DatePickerMode.Since:
                  return (
                    <div sel='.control-wrapper.since'>
                      <div sel='.control-row-wrapper'>
                        <InputEl
                          sel='.start-date-text-input'
                          attrs={{
                            'auto-focus': !this.state.fromDateText,
                            type: `text`,
                            placeholder: this.t(`reports.date-range.custom.since-date-placeholder`),
                            elref: `sinceFromInput`,
                          }}
                          props={{
                            value: this.state.fromDateText,
                          }}
                          on={{
                            input: this.helpers.handleFromDateInput,
                            focus: () => this.handleFromDateFocus(),
                            blur: () => this.handleFromDateBlur(),
                            keydown: this.helpers.handleFromDateArrowKeys,
                          }}
                        />
                        {this.state.isTimePickerEnabled && (
                          <InputEl
                            sel='.time-text-input'
                            attrs={{
                              'auto-focus': !this.state.fromTimeText,
                              type: `text`,
                              placeholder: this.t(`reports.date-range.custom.time-placeholder`),
                              elref: `sinceFromTimeInput`,
                            }}
                            props={{
                              value: this.state.fromTimeText,
                            }}
                            on={{
                              input: this.helpers.handleFromTimeInput,
                              blur: () => this.handleFromTimeBlur(),
                              keydown: this.helpers.handleFromTimeArrowKeys,
                            }}
                          />
                        )}
                      </div>
                    </div>
                  );
              }
            })()}
          </div>
          <div sel='.calendar-container'>
            <ScrollingCalendarEl
              sel='.calendar'
              class={{'is-hidden': shouldHideCalendarPreview}}
              style={{'--calendar-height': `${260 - (this.attr(`reduce-height`) ? MENU_HEIGHT_REDUCTION : 0)}px`}}
              attrs={{
                'start-of-week': this.helpers.getStartOfWeek(),
                today: this.helpers.getTodayDateString(),
                'disabled-until-date': this.helpers.getMaximumDateIso(),
                'disabled-start-date': this.helpers.getDisabledStartIso(),
                selection,
                'preview-selection': previewSelection,
                elref: `calendar`,
              }}
              on={{
                dateclick: this.helpers.handleCalendarDateClick,
                datemouseenter: this.helpers.handleCalendarDateMouseEnter,
                datemouseleave: this.helpers.handleCalendarDateMouseLeave,
              }}
              hook={{
                insert: this.helpers.handleCalendarElementInsert,
                remove: this.helpers.handleCalendarElementRemove,
              }}
              props={{dayTooltipRenderer: this.helpers.renderCalendarDayTooltip}}
            />
            {this.helpers.shouldShowQueryDaysLimit() && (
              <BannerEl
                attrs={{
                  theme: this.helpers.BannerTheme.Warn,
                  type: this.helpers.BannerType.Embedded,
                  size: this.helpers.BannerSize.ExtraSmall,
                  'body-text': this.t(`reports.date-range.custom.tooltip.max-days`, {
                    count: this.helpers.getQueryMaxDays(),
                  }),
                }}
              />
            )}
            {this.helpers.shouldShowMaxHistoryBanner() && (
              <BannerEl
                attrs={{
                  theme: this.helpers.BannerTheme.Warn,
                  type: this.helpers.BannerType.Embedded,
                  size: this.helpers.BannerSize.ExtraSmall,
                  'body-text': this.t(`reports.date-range.custom.tooltip.time-limit`, {
                    count: this.attr(`max-history-days`),
                  }),
                }}
              />
            )}
            {this.helpers.shouldShowUpsell() && (
              <BannerEl
                sel='.upsell-banner'
                attrs={{
                  theme: this.helpers.BannerTheme.Warn,
                  type: this.helpers.BannerType.Embedded,
                  size: this.helpers.BannerSize.ExtraSmall,
                  'body-text': this.t(`reports.date-range.custom.tooltip.history-limit`, {
                    count: this.attr(`max-history-days`),
                  }),
                }}
                hook={{
                  insert: this.helpers.handleUpsellBannerInsert,
                }}
              />
            )}
          </div>
          <div sel='.actions'>
            {this.canUseTimePicker() && (
              <>
                <ButtonEl
                  sel='.advanced'
                  attrs={{
                    elref: `advancedButton`,
                    icon: `settings`,
                    theme: ButtonTheme.Tertiary,
                  }}
                  on={{
                    click: this.helpers.handleAdvancedClick,
                  }}
                >
                  {this.t(`reports.date-range.custom.advanced-options`)}
                </ButtonEl>
                <DropMenuEl
                  attrs={{
                    elref: `advancedDropdownMenu`,
                    open: this.state.isAdvancedMenuOpen,
                  }}
                  on={{
                    change: (ev: CustomEvent) => this.update({isAdvancedMenuOpen: Visibility.Open === ev.detail.state}),
                  }}
                >
                  {this.state.isAdvancedMenuOpen && (
                    <ItemsMenuEl
                      attrs={{
                        elref: `advancedDropdownMenuItems`,
                      }}
                      props={{
                        sections: [
                          {
                            items: [
                              {
                                isDisabled: this.attr(`disable-sub-day-granularity-message`) !== ``,
                                label: this.state.isTimePickerEnabled
                                  ? this.t(`reports.date-range.custom.advanced.disable-time-ranges`)
                                  : this.t(`reports.date-range.custom.advanced.enable-time-ranges`),
                                tooltip: {
                                  text: this.helpers.getTimeRangesTooltipText(),
                                },
                                value: {
                                  advancedMenuItemType: AdvancedMenuItem.EnableDisableTimePicker,
                                },
                              } as SectionItem,
                            ],
                          } as Section,
                        ],
                      }}
                      on={{
                        clickItem: (ev) => {
                          if (ev.detail.value.advancedMenuItemType === AdvancedMenuItem.EnableDisableTimePicker) {
                            // If the time picker is currently enabled, it's about to be disabled,
                            // so clear the time portion of the from/toDates.
                            if (this.state.isTimePickerEnabled) {
                              this.helpers.clearTimeFromDates();
                            } else {
                              this.helpers.addTimeToDates();
                            }
                            this.update({
                              isTimePickerEnabled: !this.state.isTimePickerEnabled,
                            });
                          } else {
                            console.error(
                              `ERROR: Unexpected or absent advancedMenuItemType: '${ev.detail.value.advancedMenuItemType}' from value: '${ev.detail.value}'`,
                            );
                          }
                          this.update({
                            isAdvancedMenuOpen: false,
                          });
                        },
                      }}
                    />
                  )}
                </DropMenuEl>
              </>
            )}
            <ButtonEl
              sel='.cancel'
              attrs={{
                theme: ButtonTheme.Tertiary,
                elref: `cancelButton`,
              }}
              on={{
                click: this.helpers.handleCancelClick,
              }}
            >
              {this.t(`common.cancel`)}
            </ButtonEl>
            {this.helpers.shouldShowUpsell() ? (
              <a
                attrs={{
                  href: `/pricing`,
                  target: `_blank`,
                }}
                on={{click: this.helpers.handleUpsellClick}}
              >
                <ButtonEl
                  attrs={{
                    icon: `share`,
                    theme: ButtonTheme.Tertiary,
                    elref: `upsellButton`,
                  }}
                >
                  {this.t(`common.upgrade`)}
                </ButtonEl>
              </a>
            ) : (
              <ButtonEl
                sel='.apply'
                attrs={{
                  theme: ButtonTheme.Primary,
                  disabled: !this.helpers.canApply(),
                  elref: `applyButton`,
                }}
                on={{
                  click: this.helpers.handleApplyClick,
                }}
              >
                {this.t(`common.apply`)}
              </ButtonEl>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
